// Worker Queue Management with Circuit Breaker Pattern
export interface QueueTask {
  id: string;
  type: 'promptpay' | 'ocr' | 'verification';
  data: any;
  priority: 'high' | 'medium' | 'low';
  attempts: number;
  maxRetries: number;
  createdAt: Date;
  scheduledAt?: Date;
  timeout?: number;
}

export interface CircuitBreakerConfig {
  failureThreshold: number;
  resetTimeout: number;
  monitoringPeriod: number;
}

export interface WorkerMetrics {
  totalTasks: number;
  completedTasks: number;
  failedTasks: number;
  averageProcessingTime: number;
  circuitBreakerStatus: 'closed' | 'open' | 'half-open';
  lastFailureTime?: Date;
}

export class WorkerQueue {
  private queue: QueueTask[] = [];
  private processing: Set<string> = new Set();
  private metrics: WorkerMetrics = {
    totalTasks: 0,
    completedTasks: 0,
    failedTasks: 0,
    averageProcessingTime: 0,
    circuitBreakerStatus: 'closed'
  };
  private circuitBreakerConfig: CircuitBreakerConfig = {
    failureThreshold: 5,
    resetTimeout: 60000,
    monitoringPeriod: 30000
  };
  private failureCount = 0;
  private lastFailureTime?: Date;
  private processingTimes: number[] = [];

  // Add task to queue
  addTask(task: Omit<QueueTask, 'id' | 'attempts' | 'createdAt'>): string {
    const taskId = this.generateTaskId();
    const fullTask: QueueTask = {
      ...task,
      id: taskId,
      attempts: 0,
      createdAt: new Date()
    };

    // Insert based on priority
    this.insertByPriority(fullTask);
    this.metrics.totalTasks++;
    
    return taskId;
  }

  // Generate unique task ID
  private generateTaskId(): string {
    return 'task_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Insert task by priority
  private insertByPriority(task: QueueTask): void {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    const taskPriority = priorityOrder[task.priority];
    
    let insertIndex = this.queue.length;
    for (let i = 0; i < this.queue.length; i++) {
      if (taskPriority < priorityOrder[this.queue[i].priority]) {
        insertIndex = i;
        break;
      }
    }
    
    this.queue.splice(insertIndex, 0, task);
  }

  // Process next task in queue
  async processNext(): Promise<boolean> {
    if (this.queue.length === 0) {
      return false;
    }

    // Check circuit breaker
    if (this.isCircuitBreakerOpen()) {
      return false;
    }

    const task = this.queue.shift();
    if (!task) {
      return false;
    }

    this.processing.add(task.id);
    const startTime = Date.now();

    try {
      await this.executeTask(task);
      this.recordSuccess(Date.now() - startTime);
      return true;
    } catch (error) {
      this.recordFailure();
      
      // Retry logic
      if (task.attempts < task.maxRetries) {
        task.attempts++;
        task.scheduledAt = new Date(Date.now() + this.calculateBackoffDelay(task.attempts));
        this.insertByPriority(task);
      }
      return false;
      
    } finally {
      this.processing.delete(task.id);
    }
  }

  // Execute specific task type
  private async executeTask(task: QueueTask): Promise<void> {
    switch (task.type) {
      case 'promptpay':
        return this.executePromptPayTask(task);
      case 'ocr':
        return this.executeOCRTask(task);
      case 'verification':
        return this.executeVerificationTask(task);
      default:
        throw new Error('Unknown task type: ' + task.type);
    }
  }

  private async executePromptPayTask(task: QueueTask): Promise<void> {
    const { amount, phone, reference } = task.data;
    
    // Simulate PromptPay API call with timeout
    const response = await this.fetchWithTimeout(
      'https://api.promptpay.co.th/v1/payment',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + this.getAuthToken()
        },
        body: JSON.stringify({
          amount,
          phone,
          reference,
          timestamp: new Date().toISOString()
        })
      },
      task.timeout || 30000
    );

    if (!response.ok) {
      throw new Error('PromptPay API error: ' + response.status + ' ' + response.statusText);
    }

    const result = await response.json();
  }

  private async executeOCRTask(task: QueueTask): Promise<void> {
    // OCR processing would be handled by the OCR worker
    // This is a placeholder for the queue system
    await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate processing
  }

  private async executeVerificationTask(task: QueueTask): Promise<void> {
    const { extractedData, expectedData } = task.data;
    
    // Simple verification logic
    const amountMatch = extractedData.amount === expectedData.amount;
    const phoneMatch = extractedData.phone === expectedData.phone;
    const referenceMatch = extractedData.reference === expectedData.reference;
    
    const verification = {
      isValid: amountMatch && phoneMatch && referenceMatch,
      mismatches: {
        amount: !amountMatch,
        phone: !phoneMatch,
        reference: !referenceMatch
      }
    };
    
    if (!verification.isValid) {
      throw new Error('Verification failed: Data mismatch');
    }
  }

  // Circuit breaker methods
  private isCircuitBreakerOpen(): boolean {
    if (this.metrics.circuitBreakerStatus === 'open') {
      if (this.lastFailureTime && 
          Date.now() - this.lastFailureTime.getTime() > this.circuitBreakerConfig.resetTimeout) {
        this.metrics.circuitBreakerStatus = 'half-open';
        return false;
      }
      return true;
    }
    return false;
  }

  private recordSuccess(processingTime: number): void {
    this.metrics.completedTasks++;
    this.metrics.circuitBreakerStatus = 'closed';
    this.failureCount = 0;
    this.updateAverageProcessingTime(processingTime);
  }

  private recordFailure(): void {
    this.metrics.failedTasks++;
    this.failureCount++;
    this.lastFailureTime = new Date();
    
    if (this.failureCount >= this.circuitBreakerConfig.failureThreshold) {
      this.metrics.circuitBreakerStatus = 'open';
    }
  }

  private calculateBackoffDelay(attempt: number): number {
    return Math.min(1000 * Math.pow(2, attempt), 30000); // Max 30 seconds
  }

  private updateAverageProcessingTime(processingTime: number): void {
    this.processingTimes.push(processingTime);
    if (this.processingTimes.length > 100) {
      this.processingTimes.shift();
    }
    this.metrics.averageProcessingTime = this.processingTimes.reduce((a, b) => a + b, 0) / this.processingTimes.length;
  }

  // Helper methods
  private async fetchWithTimeout(url: string, options: RequestInit, timeout: number): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  private getAuthToken(): string {
    return 'dummy_token'; // In production, this would fetch from auth service
  }

  // Public methods
  getMetrics(): WorkerMetrics {
    return { ...this.metrics };
  }

  getQueueStatus(): {
    totalTasks: number;
    processingTasks: number;
    pendingTasks: number;
    completedTasks: number;
    failedTasks: number;
  } {
    return {
      totalTasks: this.metrics.totalTasks,
      processingTasks: this.processing.size,
      pendingTasks: this.queue.length,
      completedTasks: this.metrics.completedTasks,
      failedTasks: this.metrics.failedTasks
    };
  }

  async startProcessor(intervalMs: number = 1000): Promise<void> {
    setInterval(async () => {
      if (this.queue.length > 0 && !this.isCircuitBreakerOpen()) {
        await this.processNext();
      }
    }, intervalMs);
  }
}

// Export singleton instance
export const workerQueue = new WorkerQueue();
